import React, { useState } from 'react'

import {
  Alert,
  Button,
  Card,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'

import { Page } from 'src/components/base'
import useSettings from 'src/hooks/useSettings'
import {
  useCreatePat,
  useGetPats,
  useRevokePat
} from 'src/services/Query/auth/auth.query'

const formatDateTime = (value: string | null) => {
  if (!value) {
    return 'never'
  }

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return value
  }

  return date.toLocaleString()
}

const ApiTokens: React.FC = () => {
  const { themeStretch } = useSettings()

  const { data: tokens, isLoading, isError } = useGetPats()
  const createPat = useCreatePat()
  const revokePat = useRevokePat()

  const [createOpen, setCreateOpen] = useState(false)
  const [label, setLabel] = useState('')
  const [expiresInDays, setExpiresInDays] = useState('')
  const [createdToken, setCreatedToken] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const hasTokens = !!tokens && tokens.length > 0

  const resetCreateForm = () => {
    setLabel('')
    setExpiresInDays('')
  }

  const handleCreate = async () => {
    const days = Number(expiresInDays)
    const created = await createPat.mutateAsync({
      label: label.trim() || undefined,
      expiresInDays: expiresInDays && days > 0 ? days : undefined
    })
    setCreateOpen(false)
    resetCreateForm()
    setCreatedToken(created.token)
    setCopied(false)
  }

  const handleCopy = async () => {
    if (!createdToken) {
      return
    }

    try {
      await navigator.clipboard.writeText(createdToken)
      setCopied(true)
    } catch (error) {
      console.error('Failed to copy token', error)
    }
  }

  const handleRevoke = async (id: string) => {
    if (
      !window.confirm(
        'Revoke this token? Any client using it will stop working.'
      )
    ) {
      return
    }

    try {
      await revokePat.mutateAsync(id)
    } catch (error) {
      console.error('Failed to revoke token', error)
    }
  }

  return (
    <Page title="API Tokens">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
        >
          <Typography variant="h4">API Tokens</Typography>
          <Button variant="contained" onClick={() => setCreateOpen(true)}>
            Create token
          </Button>
        </Stack>

        <Typography variant="body2" color="text.secondary" mb={2}>
          Personal access tokens let scripts and integrations use the TimeBot
          API on your behalf. Send one as an <code>Authorization: Bearer</code>{' '}
          header. A token is shown only once, at creation.
        </Typography>

        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Label</TableCell>
                  <TableCell>Created at</TableCell>
                  <TableCell>Last used</TableCell>
                  <TableCell>Expires at</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={5}>Loading tokens...</TableCell>
                  </TableRow>
                )}

                {isError && !isLoading && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      Failed to load tokens. Please try again.
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading && !isError && !hasTokens && (
                  <TableRow>
                    <TableCell colSpan={5}>No tokens yet.</TableCell>
                  </TableRow>
                )}

                {!isLoading &&
                  !isError &&
                  tokens?.map((token) => (
                    <TableRow key={token.id}>
                      <TableCell>{token.label || '-'}</TableCell>
                      <TableCell>{formatDateTime(token.createdAt)}</TableCell>
                      <TableCell>{formatDateTime(token.lastUsedAt)}</TableCell>
                      <TableCell>{formatDateTime(token.expiresAt)}</TableCell>
                      <TableCell align="right">
                        <Button
                          color="error"
                          size="small"
                          disabled={revokePat.isLoading}
                          onClick={() => handleRevoke(token.id)}
                        >
                          Revoke
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>

      {/* Create dialog */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create API token</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Label"
              placeholder="e.g. timelog skill"
              fullWidth
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
            <TextField
              label="Expires in (days)"
              placeholder="Leave empty for no expiry"
              type="number"
              fullWidth
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(e.target.value)}
              inputProps={{ min: 1 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={createPat.isLoading}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reveal dialog — shown once */}
      <Dialog
        open={!!createdToken}
        onClose={() => setCreatedToken(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Copy your new token</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This is the only time the token is shown. Copy it now and store it
            somewhere safe — you won&apos;t be able to see it again.
          </Alert>
          <TextField
            fullWidth
            value={createdToken ?? ''}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={copied ? 'Copied!' : 'Copy'}>
                    <IconButton onClick={handleCopy} edge="end">
                      {copied ? '✓' : '⧉'}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              )
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setCreatedToken(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  )
}

export default ApiTokens
